import { useWorkerMessages } from "@/features/workers/hooks/useWorkerMessages";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Send, ImagePlus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/clock-in/$clockInId/chat/")({
  component: ChatComponent,
});

const IMAGE_ONLY_MESSAGE_PLACEHOLDER = "(Image)";

function ChatComponent() {
  const { clockInId } = Route.useParams();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedImage, setSelectedImage] = useState<{
    file: File;
    preview: string;
  } | null>(null);

  // Get clock-in data to extract worker info
  const [clockIn, setClockIn] = useState<any>(null);
  const [worker, setWorker] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  // Load clock-in data
  useEffect(() => {
    const loadClockInData = async () => {
      try {
        const { doc, onSnapshot } = await import("firebase/firestore");
        const { db } = await import("@/firebase");
        
        const clockInRef = doc(db, "clockIns", clockInId);
        const unsubscribe = onSnapshot(clockInRef, async (docSnapshot) => {
          if (docSnapshot.exists()) {
            const clockInData = {
              clockInId: docSnapshot.id,
              ...docSnapshot.data(),
            };
            setClockIn(clockInData);

            // Load worker data
            if ((clockInData as any).workerId) {
              const workerRef = doc(db, "Workers", (clockInData as any).workerId);
              const workerUnsubscribe = onSnapshot(workerRef, (workerDoc) => {
                if (workerDoc.exists()) {
                  setWorker(workerDoc.data());
                }
                setLoading(false);
              });
              return () => workerUnsubscribe();
            }
          }
          setLoading(false);
        });

        return () => unsubscribe();
      } catch (error) {
        console.error("Error loading clock-in data:", error);
        setLoading(false);
      }
    };

    loadClockInData();
  }, [clockInId]);

  // Load conversation
  const workerId = (clockIn as any)?.workerId as string | undefined;
  const { conversation, loading: conversationLoading } = useWorkerMessages(workerId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [conversation?.messages]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSelectedImage({
        file,
        preview: reader.result as string,
      });
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();

    if ((!message.trim() && !selectedImage) || !(clockIn as any)?.workerId || !worker) {
      return;
    }

    setSending(true);

    try {
      let imageBase64: string | undefined;
      let mimeType: string | undefined;

      if (selectedImage) {
        // Convert image to base64
        const base64Data = selectedImage.preview.split(",")[1];
        imageBase64 = base64Data;
        mimeType = selectedImage.file.type;
      }

      // Get Firebase Functions URL
      const functionsUrl = import.meta.env.VITE_USE_EMULATORS
        ? `http://127.0.0.1:5001/${import.meta.env.VITE_FIREBASE_PROJECT_ID}/us-central1/api`
        : `https://us-central1-${import.meta.env.VITE_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;

      const response = await fetch(`${functionsUrl}/worker-message`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          workerId: (clockIn as any).workerId,
          workerName: worker.name,
          workerPhoneNumber: worker.phoneNumber,
          message: message.trim() || IMAGE_ONLY_MESSAGE_PLACEHOLDER,
          clockInId,
          imageBase64,
          mimeType,
        }),
      });

      const data = await response.json();

      if (data.success) {
        setMessage("");
        setSelectedImage(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      } else {
        throw new Error(data.error || "Failed to send message");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  if (loading || conversationLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-32 w-32 animate-spin rounded-full border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  if (!clockIn || !worker) {
    return (
      <div className="flex min-h-screen items-center justify-center p-8">
        <div className="text-center">
          <h2 className="mb-2 text-xl font-semibold">Invalid Link</h2>
          <p className="text-gray-600">Unable to load chat information.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      {/* Header */}
      <div className="bg-primary sticky top-0 z-10 px-4 py-4 text-white shadow-md">
        <div className="mx-auto flex max-w-2xl items-center gap-3">
          <Link
            to="/clock-in/$clockInId"
            params={{ clockInId }}
            className="flex items-center gap-2 text-white hover:opacity-80"
          >
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-lg font-semibold">Chat with Handle Team</h1>
            <p className="text-sm opacity-90">{worker.name}</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-2xl space-y-4">
          {conversation?.messages && conversation.messages.length > 0 ? (
            <>
              {conversation.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex ${msg.sender === "worker" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={`max-w-[80%] rounded-lg px-4 py-3 ${
                      msg.sender === "worker"
                        ? "bg-primary text-white"
                        : "bg-white text-gray-800 shadow-sm"
                    }`}
                  >
                    {msg.sender === "admin" && msg.senderName && (
                      <p className="mb-1 text-xs font-medium text-gray-500">
                        {msg.senderName} (Handle Team)
                      </p>
                    )}
                    {msg.imageUrl && (
                      <img
                        src={msg.imageUrl}
                        alt="Attachment"
                        className="mb-2 max-h-64 rounded-lg cursor-pointer"
                        onClick={() => window.open(msg.imageUrl, "_blank")}
                      />
                    )}
                    <p className="whitespace-pre-wrap break-words text-sm">{msg.text}</p>
                    <p
                      className={`mt-1 text-xs ${
                        msg.sender === "worker" ? "text-green-100" : "text-gray-400"
                      }`}
                    >
                      {msg.timestamp.toDate().toLocaleString("en-US", {
                        month: "short",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </>
          ) : (
            <div className="py-12 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-200">
                <svg
                  className="h-8 w-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-700">No messages yet</h3>
              <p className="text-sm text-gray-500">
                Send a message to the Handle team and they'll get back to you!
              </p>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Message Input */}
      <div className="sticky bottom-0 border-t bg-white p-4 shadow-lg">
        <div className="mx-auto max-w-2xl">
          {/* Image Preview */}
          {selectedImage && (
            <div className="mb-3 relative inline-block">
              <img
                src={selectedImage.preview}
                alt="Preview"
                className="max-h-32 rounded-lg border-2 border-green-500"
              />
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleImageSelect}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || !!selectedImage}
              className="bg-gray-100 flex items-center justify-center rounded-full p-3 text-gray-600 transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ImagePlus className="h-5 w-5" />
            </button>
            <input
              type="text"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 rounded-full border border-gray-300 px-4 py-3 text-sm focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-200"
              maxLength={500}
              disabled={sending}
            />
            <button
              type="submit"
              disabled={(!message.trim() && !selectedImage) || sending}
              className="bg-primary flex items-center justify-center rounded-full p-3 text-white transition-colors hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {sending ? (
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
              ) : (
                <Send className="h-5 w-5" />
              )}
            </button>
          </form>
          <p className="mt-2 text-center text-xs text-gray-500">
            {message.length}/500 characters
          </p>
        </div>
      </div>
    </div>
  );
}
